import type {ComponentType} from 'react';

import {useTranslation} from 'react-i18next';

import {Chip} from 'UI/components/action/Chip';
import {HorizontalScroll} from 'UI/components/layout/HorizontalScroll';

import {CATEGORY_ICON_MAP} from 'UI/utils/categoryIconMap';

import type {Category} from 'types/payload-types';

/** Map of predefined icon names to their React components. */

const ICON_MAP: Record<string, ComponentType<any>> = CATEGORY_ICON_MAP;

/**
 * Custom hook backing the `CategoryFilterBlock` component. Hosts the single `useTranslation`
 * call — required by the `@nfq/component-single-hook` lint rule, which limits the component
 * body itself to one hook call plus guard clauses/returns — and derives the populated
 * categories and special filter chip labels.
 *
 * @param categories         Array of Category documents (or unpopulated string IDs).
 * @param showSpecialFilters Whether to include the special filters (e.g. "Kostenlos", "Kurz").
 * @returns The populated Category documents and the special filter chip labels.
 */
const useCategoryFilterBlock = (categories: (Category | string)[] | null | undefined, showSpecialFilters: boolean) => {
    const {t} = useTranslation();
    const populatedCategories = (categories ?? []).filter((c): c is Category => typeof c === 'object');
    const specialChips: string[] = showSpecialFilters ? [t('home.filterFree'), t('home.filterShort')] : [];

    return {
        populatedCategories,
        specialChips
    };
};

/**
 * Props for the CategoryFilterBlock component.
 */
interface ComponentProps {
    /** Active filter value (managed by parent). */
    activeFilter?: string | null;
    /** Array of populated Category documents to display as chips. */
    categories?: (Category | string)[] | null;
    /** Callback when a chip is clicked. */
    onChipClick?(label: string): void;
    /** Whether to show special filters like "Kostenlos", "Kurz". */
    showSpecialFilters?: boolean;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     *
     * @default 'CategoryFilterBlock'
     */
    testId?: string;
}

/**
 * Renders a horizontal row of filter chips based on categories from the CMS.
 * Shows predefined icons from the project when configured on the category.
 * Optionally includes special filters for free and short walks.
 *
 * @param props                    The component props.
 * @param props.activeFilter       The currently active filter label.
 * @param props.categories         Array of Category documents.
 * @param props.onChipClick        Callback when a chip is clicked.
 * @param props.showSpecialFilters Whether to show additional filters.
 * @param props.testId             An optional test ID. Defaults to `'CategoryFilterBlock'`.
 * @returns A JSX element rendering the category filter chips.
 *
 * @example
 * ```tsx
 * <CategoryFilterBlock categories={categories} showSpecialFilters />
 * ```
 */
const CategoryFilterBlock = ({
    activeFilter,
    categories,
    onChipClick,
    showSpecialFilters = true,
    testId = 'CategoryFilterBlock'
}: ComponentProps) => {
    const {populatedCategories, specialChips} = useCategoryFilterBlock(categories, showSpecialFilters);

    if (populatedCategories.length === 0 && specialChips.length === 0) return null;

    return (
        <HorizontalScroll data-cy={testId} gap={3}>
            {populatedCategories.map(cat => {
                const IconComponent = cat.predefinedIcon ? ICON_MAP[cat.predefinedIcon] : null;

                return (
                    <Chip
                        key={cat.id}
                        icon={IconComponent ? <IconComponent /> : undefined}
                        isActive={activeFilter === cat.name}
                        label={cat.name}
                        onClick={() => onChipClick?.(cat.name)}
                    />
                );
            })}
            {specialChips.map(label => (
                <Chip
                    key={label}
                    isActive={activeFilter === label}
                    label={label}
                    onClick={() => onChipClick?.(label)}
                />
            ))}
        </HorizontalScroll>
    );
};

CategoryFilterBlock.displayName = 'CategoryFilterBlock';

export {CategoryFilterBlock};